// src/pages/settings/ReceiptApprovalsPage.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  FiCheck,
  FiEye,
  FiRefreshCw,
  FiSearch,
  FiX,
} from "react-icons/fi";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  toast,
} from "react-toastify";

import {
  getPerm,
} from "../../../utils/perm";

import {
  approveReceiptThunk,
  clearReceiptApprovalError,
  fetchPendingReceiptsThunk,
  rejectReceiptThunk,
} from "../../../store/slices/receiptApprovalsSlice";

import "../../../css/master-common.css";

// ============================================================
// HELPERS
// ============================================================

const money = (value) => {
  const n = Number(value);

  return Number.isFinite(n)
    ? n.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "0.00";
};

const textValue = (
  value,
  fallback = "-"
) => {
  const text =
    String(value ?? "").trim();

  return text || fallback;
};

const paymentModeLabel = (
  value
) => {
  const map = {
    cash: "Cash",
    cheque: "Cheque",
    bank_transfer:
      "Bank Transfer",
    upi: "UPI",
    other: "Other",
  };

  return (
    map[
      String(
        value || ""
      ).toLowerCase()
    ] ||
    textValue(value)
  );
};

// ============================================================
// PAGE
// ============================================================

export default function ReceiptApprovalsPage() {
  const dispatch =
    useDispatch();

  // ==========================================================
  // ACTIVE PANCHAYATH
  // ==========================================================

  const activePanId =
    useSelector(
      (state) =>
        Number(
          state.tenant
            ?.pan_id_active ||
            0
        )
    );

  // ==========================================================
  // REDUX
  // ==========================================================

  const {
    rows,
    pagination,
    loading,
    approving,
    rejecting,
    actionReceiptId,
    error,
  } = useSelector(
    (state) =>
      state.receiptApprovals
  );

  // ==========================================================
  // PERMISSION
  // ==========================================================

  const permsMap =
    useSelector(
      (state) =>
        state.permissions?.map
    ) || {};

  const perms =
    getPerm(
      permsMap,
      "receipt_approvals"
    ) || {};

  const canView =
    Boolean(
      perms.can_view
    );

  // Only Superadmin can approve or reject receipts.
  // Panchayath users can still open this page (with View permission)
  // to see what is pending, but the Approve / Reject buttons stay disabled.
  const userRole =
    useSelector(
      (state) =>
        String(
          state.auth?.user?.role || ""
        )
          .trim()
          .toLowerCase()
    );

  const isSuperadmin =
    userRole === "superadmin";

  const canUpdate =
    isSuperadmin;

  // ==========================================================
  // FILTERS
  // ==========================================================

  const [
    searchInput,
    setSearchInput,
  ] = useState("");

  const [
    filters,
    setFilters,
  ] = useState({
    page: 1,
    pageSize: 50,
    search: "",
  });

  // ==========================================================
  // MODALS
  // ==========================================================

  const [
    viewRow,
    setViewRow,
  ] = useState(null);

  const [
    approveRow,
    setApproveRow,
  ] = useState(null);

  const [
    rejectRow,
    setRejectRow,
  ] = useState(null);

  const [
    rejectionReason,
    setRejectionReason,
  ] = useState("");

  // ==========================================================
  // REQUEST PARAMS
  // ==========================================================

  const requestFilters =
    useMemo(
      () => ({
        page:
          filters.page,

        pageSize:
          filters.pageSize,

        pan_id:
          activePanId ||
          "",

        search:
          filters.search ||
          "",
      }),
      [
        filters,
        activePanId,
      ]
    );

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {
    if (!canView) {
      return;
    }

    dispatch(
      fetchPendingReceiptsThunk(
        requestFilters
      )
    );
  }, [
    dispatch,
    requestFilters,
    canView,
  ]);

  // ==========================================================
  // ERROR TOAST
  // ==========================================================

  useEffect(() => {
    if (!error) {
      return;
    }

    toast.error(
      String(error)
    );

    dispatch(
      clearReceiptApprovalError()
    );
  }, [
    error,
    dispatch,
  ]);

  // ==========================================================
  // SEARCH
  // ==========================================================

  const applySearch = () => {
    setFilters(
      (previous) => ({
        ...previous,
        page: 1,
        search:
          searchInput.trim(),
      })
    );
  };

  const clearSearch = () => {
    setSearchInput("");

    setFilters(
      (previous) => ({
        ...previous,
        page: 1,
        search: "",
      })
    );
  };

  const refresh = () => {
    dispatch(
      fetchPendingReceiptsThunk(
        requestFilters
      )
    );
  };

  // ==========================================================
  // APPROVE
  // ==========================================================

  const confirmApprove =
    async () => {
      if (
        !approveRow
          ?.receipt_id
      ) {
        return;
      }

      if (!canUpdate) {
        toast.error(
          "Only Superadmin can approve or reject receipts"
        );

        return;
      }

      const result =
        await dispatch(
          approveReceiptThunk(
            approveRow
              .receipt_id
          )
        );

      if (
        approveReceiptThunk
          .fulfilled
          .match(result)
      ) {
        toast.success(
          result.payload
            ?.message ||
            "Receipt approved successfully"
        );

        setApproveRow(
          null
        );

        dispatch(
          fetchPendingReceiptsThunk(
            requestFilters
          )
        );
      }
    };

  // ==========================================================
  // REJECT
  // ==========================================================

  const confirmReject =
    async () => {
      if (
        !rejectRow
          ?.receipt_id
      ) {
        return;
      }

      if (!canUpdate) {
        toast.error(
          "Only Superadmin can approve or reject receipts"
        );

        return;
      }

      const reason =
        rejectionReason.trim();

      if (!reason) {
        toast.error(
          "Rejection Reason is required"
        );

        return;
      }

      const result =
        await dispatch(
          rejectReceiptThunk({
            receiptId:
              rejectRow
                .receipt_id,

            rejection_reason:
              reason,
          })
        );

      if (
        rejectReceiptThunk
          .fulfilled
          .match(result)
      ) {
        toast.success(
          result.payload
            ?.message ||
            "Receipt rejected successfully"
        );

        setRejectRow(
          null
        );

        setRejectionReason(
          ""
        );

        dispatch(
          fetchPendingReceiptsThunk(
            requestFilters
          )
        );
      }
    };

  // ==========================================================
  // PAGINATION
  // ==========================================================

  const totalPages =
    Math.max(
      1,
      Number(
        pagination
          ?.totalPages ||
          1
      )
    );

  const currentPage =
    Math.max(
      1,
      Number(
        pagination?.page ||
        filters.page ||
        1
      )
    );

  const paginationItems =
    useMemo(() => {
      const pages = [];

      const start =
        Math.max(
          1,
          currentPage - 2
        );

      const end =
        Math.min(
          totalPages,
          currentPage + 2
        );

      for (
        let page = start;
        page <= end;
        page += 1
      ) {
        pages.push(
          page
        );
      }

      return pages;
    }, [
      currentPage,
      totalPages,
    ]);

  const goToPage = (
    page
  ) => {
    const nextPage =
      Math.min(
        totalPages,
        Math.max(
          1,
          Number(page) ||
            1
        )
      );

    setFilters(
      (previous) => ({
        ...previous,
        page:
          nextPage,
      })
    );
  };

  // ==========================================================
  // ACCESS
  // ==========================================================

  if (!canView) {
    return (
      <div className="hca-page">
        <Card className="hca-card">
          <Card.Body>
            <div className="hca-empty-cell">
              <div className="hca-empty-icon">
                <FiX size={20} />
              </div>

              <strong>
                Access Denied
              </strong>

              <span>
                You do not have permission to view Receipt Approvals.
              </span>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="hca-page">
      <style>{`
        .hca-actions-with-text {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: nowrap;
          min-width: 290px;
        }

        .hca-actions .hca-icon-btn.hca-action-text-btn {
          width: auto !important;
          min-width: max-content !important;
          height: 34px !important;
          min-height: 34px !important;
          padding: 6px 10px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 5px !important;
          border-radius: 6px !important;
          white-space: nowrap !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          line-height: 1 !important;
        }

        .hca-actions .hca-icon-btn.hca-action-text-btn svg {
          flex: 0 0 auto;
        }

        .hca-actions .hca-icon-btn.hca-action-text-btn span {
          display: inline-block !important;
          white-space: nowrap;
        }

        .hca-actions-th,
        .hca-actions-td {
          min-width: 300px;
        }

        @media (max-width: 1200px) {
          .hca-actions-with-text {
            min-width: 270px;
          }

          .hca-actions-th,
          .hca-actions-td {
            min-width: 280px;
          }

          .hca-actions .hca-icon-btn.hca-action-text-btn {
            padding: 6px 8px !important;
            font-size: 11px !important;
          }
        }
      `}</style>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="hca-header">
        <div>
          <h4 className="hca-title">
            Receipt Approvals
          </h4>

          <div className="hca-subtitle">
            Review Panchayath payment receipts before they affect invoice balance.
          </div>
        </div>

        <Button
          type="button"
          className="hca-refresh-btn"
          disabled={loading}
          onClick={refresh}
        >
          <FiRefreshCw
            size={15}
          />

          Refresh
        </Button>
      </div>

      {/* =====================================================
          SUMMARY
      ===================================================== */}

      <Row className="g-3 mb-3">
        <Col
          xs={12}
          md={4}
        >
          <div className="hca-stat-card">
            <div className="hca-stat-label">
              Pending Approvals
            </div>

            <div className="hca-stat-value">
              {Number(
                pagination
                  ?.total ||
                  0
              )}
            </div>
          </div>
        </Col>

        <Col
          xs={12}
          md={4}
        >
          <div className="hca-stat-card">
            <div className="hca-stat-label">
              Visible Receipt Amount
            </div>

            <div className="hca-stat-value">
              ₹
              {money(
                rows.reduce(
                  (
                    total,
                    row
                  ) =>
                    total +
                    Number(
                      row
                        ?.received_amount ||
                        0
                    ),
                  0
                )
              )}
            </div>
          </div>
        </Col>

        <Col
          xs={12}
          md={4}
        >
          <div className="hca-stat-card">
            <div className="hca-stat-label">
              Panchayath Filter
            </div>

            <div
              className="hca-stat-value"
              style={{
                fontSize:
                  18,
              }}
            >
              {activePanId
                ? `#${activePanId}`
                : "All"}
            </div>
          </div>
        </Col>
      </Row>

      {/* =====================================================
          SEARCH
      ===================================================== */}

      <Card className="hca-card mb-3">
        <Card.Body className="hca-filter-body">
          <Row className="g-3 align-items-end">
            <Col
              xs={12}
              lg={8}
            >
              <Form.Label className="hca-label">
                Search
              </Form.Label>

              <InputGroup className="hca-search-group">
                <InputGroup.Text>
                  <FiSearch
                    size={15}
                  />
                </InputGroup.Text>

                <Form.Control
                  className="hca-control"
                  value={
                    searchInput
                  }
                  placeholder="Search Panchayath, invoice number or receipt number"
                  autoComplete="off"
                  onChange={(event) =>
                    setSearchInput(
                      event
                        .target
                        .value
                    )
                  }
                  onKeyDown={(event) => {
                    if (
                      event.key ===
                      "Enter"
                    ) {
                      event.preventDefault();

                      applySearch();
                    }
                  }}
                />

                {searchInput ||
                filters.search ? (
                  <Button
                    type="button"
                    className="hca-search-clear"
                    variant="light"
                    onClick={
                      clearSearch
                    }
                  >
                    Clear
                  </Button>
                ) : null}

                <Button
                  type="button"
                  className="hca-search-btn"
                  onClick={
                    applySearch
                  }
                >
                  Search
                </Button>
              </InputGroup>
            </Col>

            <Col
              xs={12}
              lg={4}
            >
              <Form.Label className="hca-label">
                Panchayath
              </Form.Label>

              <Form.Control
                className="hca-control"
                value={
                  activePanId
                    ? `Active Panchayath #${activePanId}`
                    : "All Panchayaths"
                }
                disabled
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* =====================================================
          TABLE
      ===================================================== */}

      <Card className="hca-card">
        {loading ? (
          <div className="hca-loading">
            <Spinner
              animation="border"
            />

            <div>
              Loading pending receipts...
            </div>
          </div>
        ) : (
          <>
            <div className="hca-table-wrap">
              <Table
                hover
                responsive={false}
                className="hca-table"
              >
                <thead>
                  <tr>
                    <th>
                      Sl. No.
                    </th>

                    <th>
                      Panchayath
                    </th>

                    <th>
                      Invoice No.
                    </th>

                    <th>
                      Receipt No.
                    </th>

                    <th>
                      Receipt Date
                    </th>

                    <th>
                      Payment Mode
                    </th>

                    <th>
                      Received Amount
                    </th>

                    <th>
                      Approved Received
                    </th>

                    <th>
                      Balance
                    </th>

                    <th>
                      Submitted On
                    </th>

                    <th>
                      Status
                    </th>

                    <th className="hca-actions-th">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {!rows.length ? (
                    <tr>
                      <td
                        colSpan={12}
                        className="hca-empty-cell"
                      >
                        <div className="hca-empty-icon">
                          <FiCheck
                            size={20}
                          />
                        </div>

                        <strong>
                          No Pending Receipts
                        </strong>

                        <span>
                          There are no receipt submissions waiting for approval.
                        </span>
                      </td>
                    </tr>
                  ) : (
                    rows.map(
                      (
                        row,
                        index
                      ) => {
                        const serialNo =
                          (currentPage -
                            1) *
                            Number(
                              pagination
                                ?.pageSize ||
                                filters
                                  .pageSize
                            ) +
                          index +
                          1;

                        const isActionLoading =
                          actionReceiptId ===
                            row
                              .receipt_id &&
                          (approving ||
                            rejecting);

                        return (
                          <tr
                            key={
                              row
                                .receipt_id
                            }
                          >
                            <td>
                              {serialNo}
                            </td>

                            <td>
                              <div className="hca-house-main">
                                {textValue(
                                  row
                                    .pan_name
                                )}
                              </div>

                              <div className="hca-house-sub">
                                Panchayath #
                                {textValue(
                                  row
                                    .pan_id
                                )}
                              </div>
                            </td>

                            <td>
                              {textValue(
                                row
                                  .invoice_number
                              )}
                            </td>

                            <td>
                              {textValue(
                                row
                                  .receipt_number
                              )}
                            </td>

                            <td>
                              {textValue(
                                row
                                  .receipt_date
                              )}
                            </td>

                            <td>
                              <span className="hca-action-pill">
                                {paymentModeLabel(
                                  row
                                    .payment_mode
                                )}
                              </span>
                            </td>

                            <td>
                              <strong>
                                ₹
                                {money(
                                  row
                                    .received_amount
                                )}
                              </strong>
                            </td>

                            <td>
                              ₹
                              {money(
                                row
                                  .total_received
                              )}
                            </td>

                            <td>
                              ₹
                              {money(
                                row
                                  .balance_amount
                              )}
                            </td>

                            <td>
                              {textValue(
                                row
                                  .submitted_at
                              )}
                            </td>

                            <td>
                              <Badge className="hca-status pending">
                                Pending
                              </Badge>
                            </td>

                            <td className="hca-actions-td">
                              <div className="hca-actions hca-actions-with-text">
                                <Button
                                  type="button"
                                  className="hca-icon-btn hca-action-text-btn view"
                                  title="View Details"
                                  onClick={() =>
                                    setViewRow(
                                      row
                                    )
                                  }
                                >
                                  <FiEye
                                    size={15}
                                  />

                                  <span>
                                    View Details
                                  </span>
                                </Button>

                                <Button
                                  type="button"
                                  className="hca-icon-btn hca-action-text-btn approve"
                                  title={
                                    canUpdate
                                      ? "Approve Receipt"
                                      : "Only Superadmin can approve"
                                  }
                                  disabled={
                                    !canUpdate ||
                                    isActionLoading
                                  }
                                  onClick={() =>
                                    setApproveRow(
                                      row
                                    )
                                  }
                                >
                                  <FiCheck
                                    size={15}
                                  />

                                  <span>
                                    Approve
                                  </span>
                                </Button>

                                <Button
                                  type="button"
                                  className="hca-icon-btn hca-action-text-btn reject"
                                  title={
                                    canUpdate
                                      ? "Reject Receipt"
                                      : "Only Superadmin can reject"
                                  }
                                  disabled={
                                    !canUpdate ||
                                    isActionLoading
                                  }
                                  onClick={() => {
                                    setRejectRow(
                                      row
                                    );

                                    setRejectionReason(
                                      ""
                                    );
                                  }}
                                >
                                  <FiX
                                    size={15}
                                  />

                                  <span>
                                    Reject
                                  </span>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                    )
                  )}
                </tbody>
              </Table>
            </div>

            <div className="hca-footer">
              <div className="hca-total-text">
                Total{" "}
                <strong>
                  {Number(
                    pagination
                      ?.total ||
                      0
                  )}
                </strong>{" "}
                pending receipt
                {Number(
                  pagination
                    ?.total ||
                    0
                ) === 1
                  ? ""
                  : "s"}
              </div>

              {totalPages >
              1 ? (
                <Pagination className="hca-pagination mb-0">
                  <Pagination.Prev
                    disabled={
                      currentPage <=
                      1
                    }
                    onClick={() =>
                      goToPage(
                        currentPage -
                          1
                      )
                    }
                  />

                  {paginationItems.map(
                    (page) => (
                      <Pagination.Item
                        key={
                          page
                        }
                        active={
                          page ===
                          currentPage
                        }
                        onClick={() =>
                          goToPage(
                            page
                          )
                        }
                      >
                        {page}
                      </Pagination.Item>
                    )
                  )}

                  <Pagination.Next
                    disabled={
                      currentPage >=
                      totalPages
                    }
                    onClick={() =>
                      goToPage(
                        currentPage +
                          1
                      )
                    }
                  />
                </Pagination>
              ) : null}
            </div>
          </>
        )}
      </Card>

      {/* =====================================================
          VIEW MODAL
      ===================================================== */}

      <Modal
        show={Boolean(
          viewRow
        )}
        onHide={() =>
          setViewRow(null)
        }
        centered
        className="hca-modal"
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title>
              Receipt Details
            </Modal.Title>

            <div className="hca-modal-subtitle">
              Review submitted payment information.
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {viewRow ? (
            <>
              <div className="hca-modal-topline">
                <div>
                  <span>
                    Receipt
                  </span>

                  <strong>
                    {textValue(
                      viewRow
                        .receipt_number
                    )}
                  </strong>
                </div>

                <Badge className="hca-status pending">
                  Pending
                </Badge>
              </div>

              <Row className="g-3">
                <Col
                  xs={12}
                  md={6}
                >
                  <div className="hca-section-card">
                    <div className="hca-section-title">
                      Invoice
                    </div>

                    <div className="hca-detail-grid">
                      <div className="hca-detail-item">
                        <div className="hca-detail-label">
                          Panchayath
                        </div>

                        <div className="hca-detail-value">
                          {textValue(
                            viewRow
                              .pan_name
                          )}
                        </div>
                      </div>

                      <div className="hca-detail-item">
                        <div className="hca-detail-label">
                          Invoice No.
                        </div>

                        <div className="hca-detail-value accent">
                          {textValue(
                            viewRow
                              .invoice_number
                          )}
                        </div>
                      </div>

                      <div className="hca-detail-item">
                        <div className="hca-detail-label">
                          Invoice Amount
                        </div>

                        <div className="hca-detail-value">
                          ₹
                          {money(
                            viewRow
                              .invoice_amount
                          )}
                        </div>
                      </div>

                      <div className="hca-detail-item">
                        <div className="hca-detail-label">
                          Current Balance
                        </div>

                        <div className="hca-detail-value">
                          ₹
                          {money(
                            viewRow
                              .balance_amount
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col
                  xs={12}
                  md={6}
                >
                  <div className="hca-section-card">
                    <div className="hca-section-title">
                      Payment
                    </div>

                    <div className="hca-detail-grid">
                      <div className="hca-detail-item">
                        <div className="hca-detail-label">
                          Amount
                        </div>

                        <div className="hca-detail-value accent">
                          ₹
                          {money(
                            viewRow
                              .received_amount
                          )}
                        </div>
                      </div>

                      <div className="hca-detail-item">
                        <div className="hca-detail-label">
                          Payment Mode
                        </div>

                        <div className="hca-detail-value">
                          {paymentModeLabel(
                            viewRow
                              .payment_mode
                          )}
                        </div>
                      </div>

                      <div className="hca-detail-item">
                        <div className="hca-detail-label">
                          Receipt Date
                        </div>

                        <div className="hca-detail-value">
                          {textValue(
                            viewRow
                              .receipt_date
                          )}
                        </div>
                      </div>

                      <div className="hca-detail-item">
                        <div className="hca-detail-label">
                          Submitted On
                        </div>

                        <div className="hca-detail-value">
                          {textValue(
                            viewRow
                              .submitted_at
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col xs={12}>
                  <div className="hca-note-card">
                    <div>
                      <span>
                        Cheque No.
                      </span>

                      <strong>
                        {textValue(
                          viewRow
                            .cheque_no
                        )}
                      </strong>
                    </div>

                    <div>
                      <span>
                        Cheque Date
                      </span>

                      <strong>
                        {textValue(
                          viewRow
                            .cheque_date
                        )}
                      </strong>
                    </div>

                    <div>
                      <span>
                        Bank Name
                      </span>

                      <strong>
                        {textValue(
                          viewRow
                            .bank_name
                        )}
                      </strong>
                    </div>

                    <div>
                      <span>
                        Transaction Reference
                      </span>

                      <strong>
                        {textValue(
                          viewRow
                            .transaction_reference
                        )}
                      </strong>
                    </div>

                    <div>
                      <span>
                        Remarks
                      </span>

                      <strong>
                        {textValue(
                          viewRow
                            .remarks
                        )}
                      </strong>
                    </div>

                    <div>
                      <span>
                        Submitted By
                      </span>

                      <strong>
                        {textValue(
                          viewRow
                            .submitted_by
                        )}
                      </strong>
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            className="hca-secondary-btn"
            onClick={() =>
              setViewRow(null)
            }
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* =====================================================
          APPROVE MODAL
      ===================================================== */}

      <Modal
        show={Boolean(
          approveRow
        )}
        onHide={() => {
          if (!approving) {
            setApproveRow(
              null
            );
          }
        }}
        centered
        className="hca-modal hca-confirm-modal"
      >
        <Modal.Header closeButton={!approving}>
          <div>
            <Modal.Title>
              Approve Receipt
            </Modal.Title>

            <div className="hca-modal-subtitle">
              Approved amount will update the invoice balance.
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="hca-confirm-banner approve">
            <FiCheck
              size={18}
            />

            <div>
              <strong>
                Are you sure you want to approve this receipt?
              </strong>

              <span>
                After approval, this amount will be added to the received amount and the invoice balance will be updated.
              </span>
            </div>
          </div>

          {approveRow ? (
            <div className="hca-confirm-summary">
              <div>
                <span className="hca-detail-label">
                  Panchayath
                </span>

                <div className="hca-detail-value">
                  {textValue(
                    approveRow
                      .pan_name
                  )}
                </div>
              </div>

              <div>
                <span className="hca-detail-label">
                  Receipt No.
                </span>

                <div className="hca-detail-value">
                  {textValue(
                    approveRow
                      .receipt_number
                  )}
                </div>
              </div>

              <div>
                <span className="hca-detail-label">
                  Invoice No.
                </span>

                <div className="hca-detail-value">
                  {textValue(
                    approveRow
                      .invoice_number
                  )}
                </div>
              </div>

              <div>
                <span className="hca-detail-label">
                  Amount
                </span>

                <div className="hca-detail-value accent">
                  ₹
                  {money(
                    approveRow
                      .received_amount
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            className="hca-secondary-btn"
            disabled={approving}
            onClick={() =>
              setApproveRow(
                null
              )
            }
          >
            Cancel
          </Button>

          <Button
            type="button"
            className="hca-success-btn"
            disabled={
              approving ||
              !canUpdate
            }
            onClick={
              confirmApprove
            }
          >
            {approving ? (
              <>
                <Spinner
                  size="sm"
                  animation="border"
                  className="me-2"
                />

                Approving...
              </>
            ) : (
              "Approve Receipt"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* =====================================================
          REJECT MODAL
      ===================================================== */}

      <Modal
        show={Boolean(
          rejectRow
        )}
        onHide={() => {
          if (!rejecting) {
            setRejectRow(
              null
            );

            setRejectionReason(
              ""
            );
          }
        }}
        centered
        className="hca-modal hca-confirm-modal"
      >
        <Modal.Header closeButton={!rejecting}>
          <div>
            <Modal.Title>
              Reject Receipt
            </Modal.Title>

            <div className="hca-modal-subtitle">
              Rejected receipts do not affect the invoice balance.
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="hca-confirm-banner reject">
            <FiX
              size={18}
            />

            <div>
              <strong>
                Are you sure you want to reject this receipt?
              </strong>

              <span>
                This payment will not be counted. The Panchayath can submit another payment after rejection.
              </span>
            </div>
          </div>

          {rejectRow ? (
            <div className="hca-confirm-summary mb-3">
              <div>
                <span className="hca-detail-label">
                  Panchayath
                </span>

                <div className="hca-detail-value">
                  {textValue(
                    rejectRow
                      .pan_name
                  )}
                </div>
              </div>

              <div>
                <span className="hca-detail-label">
                  Receipt No.
                </span>

                <div className="hca-detail-value">
                  {textValue(
                    rejectRow
                      .receipt_number
                  )}
                </div>
              </div>

              <div>
                <span className="hca-detail-label">
                  Invoice No.
                </span>

                <div className="hca-detail-value">
                  {textValue(
                    rejectRow
                      .invoice_number
                  )}
                </div>
              </div>

              <div>
                <span className="hca-detail-label">
                  Amount
                </span>

                <div className="hca-detail-value accent">
                  ₹
                  {money(
                    rejectRow
                      .received_amount
                  )}
                </div>
              </div>
            </div>
          ) : null}

          <Form.Group>
            <Form.Label className="hca-label">
              Rejection Reason
            </Form.Label>

            <Form.Control
              as="textarea"
              className="hca-textarea"
              value={
                rejectionReason
              }
              disabled={
                rejecting
              }
              placeholder="Enter reason for rejecting this receipt"
              onChange={(event) =>
                setRejectionReason(
                  event.target
                    .value
                )
              }
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            className="hca-secondary-btn"
            disabled={rejecting}
            onClick={() => {
              setRejectRow(
                null
              );

              setRejectionReason(
                ""
              );
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            className="hca-danger-btn"
            disabled={
              rejecting ||
              !canUpdate ||
              !rejectionReason.trim()
            }
            onClick={
              confirmReject
            }
          >
            {rejecting ? (
              <>
                <Spinner
                  size="sm"
                  animation="border"
                  className="me-2"
                />

                Rejecting...
              </>
            ) : (
              "Reject Receipt"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
